import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  shareReplay,
  type Observable,
} from 'rxjs';
import { ViewIds } from '@ardoq/api-types';
import type { SpiderViewSettings } from '@ardoq/xyz-charts';
import { shallowEqual } from 'utils/objectUtils';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import type { ViewpointModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import {
  toPlottedViewEffects$,
  toStableSelection$,
} from 'tabview/chartShared/plottedViewEffects';
import {
  buildSpiderSourceData,
  type SpiderSelection,
} from './sourceData/buildSpiderSourceData';
import type { SpiderViewProps } from './SpiderView';

const toSpiderSelection = ({
  selectedFieldNames,
}: SpiderViewSettings): SpiderSelection => ({ selectedFieldNames });

export const spiderViewModel$ = (
  viewInstanceId: string,
  viewSettingsWithChanges$: Observable<
    ViewpointModeInstanceViewSettings<ViewIds.SPIDER_2>
  >
): Observable<SpiderViewProps> => {
  const viewSettings$ = viewSettingsWithChanges$.pipe(
    map(({ currentSettings }) => currentSettings)
  );

  const sourceData$ = combineLatest({
    loadedGraph: loadedGraph$,
    permissionContext: currentUserPermissionContext$,
    locale: locale$,
    i18nLocale: i18nLocale$,
    formattingAndLabelRules: formattingAndLabelRules$,
    viewSettings: toStableSelection$(viewSettings$, toSpiderSelection),
  }).pipe(
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    distinctUntilChanged(shallowEqual),
    map(args => buildSpiderSourceData(viewInstanceId, args)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  return merge(
    combineLatest([sourceData$, viewSettings$]).pipe(
      map(([sourceData, viewSettings]) => ({ ...sourceData, viewSettings }))
    ),
    toPlottedViewEffects$(sourceData$, ViewIds.SPIDER_2)
  );
};
